import { useEffect, useMemo, useRef, useState } from "react";
import type { Book, ReviewItem } from "../api";
import { useAuth } from "../auth";
import { ErrorNote, Spinner, relationLabel, useLoad } from "../ui";
import { ReviewBookmark, useReviewBookmark } from "./StudioPage";

type StatusFilter = "open" | "hidden" | "verified" | "all";
const STATUS_LABEL: Record<ReviewItem["status"], string> = {
  verified: "zichtbaar",
  unverified: "onzeker citaat",
  rejected: "citaat niet gevonden",
  hidden: "verborgen",
};
const TYPE_LABEL: Record<ReviewItem["item_type"], string> = {
  fact: "Feit",
  relation: "Relatie",
  event: "Gebeurtenis",
};

function displayText(item: ReviewItem): string {
  if (item.item_type !== "relation") return item.text;
  return item.text
    .split(" ")
    .map((w) => (/^[a-z]+(_[a-z]+)*$/.test(w) ? relationLabel(w, "outgoing") : w))
    .join(" ");
}

/** Split "type: text" (facts) or "title: description" (events) as built by the review gate. */
function splitText(item: ReviewItem): [string, string] {
  const i = item.text.indexOf(": ");
  return i < 0 ? ["", item.text] : [item.text.slice(0, i), item.text.slice(i + 2)];
}

export function ReviewQueue({ book }: { book: Book }) {
  const { api } = useAuth();
  const [bookmark, setBookmark] = useReviewBookmark(book);
  const items = useLoad(() => api.review(book.id, bookmark), [api, book.id, bookmark]);
  const [status, setStatus] = useState<StatusFilter>("open");
  const [type, setType] = useState<"all" | ReviewItem["item_type"]>("all");
  const [chapter, setChapter] = useState<number | "all">("all");
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Items acted on in this session: their pen mark is drawn, not just shown.
  const [just, setJust] = useState<Set<string>>(() => new Set());
  // ...and they stay in the "open" list a moment, so the mark is seen before it leaves.
  const [lingering, setLingering] = useState<Set<string>>(() => new Set());
  const listRef = useRef<HTMLUListElement>(null);

  const key = (i: ReviewItem) => `${i.item_type}-${i.item_id}`;
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items.data ?? []).filter(
      (i) =>
        (status === "all" ||
          (status === "open" &&
            (i.status === "unverified" || i.status === "rejected" || lingering.has(key(i)))) ||
          i.status === status) &&
        (type === "all" || i.item_type === type) &&
        (chapter === "all" || i.chapter_index === chapter) &&
        (!q || i.text.toLowerCase().includes(q) || i.evidence_quote.toLowerCase().includes(q)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.data, status, type, chapter, query, lingering]);
  const chapters = useMemo(
    () => [...new Set((items.data ?? []).map((i) => i.chapter_index))].sort((a, b) => a - b),
    [items.data],
  );
  const current = rows[Math.min(cursor, rows.length - 1)];

  async function act(item: ReviewItem, body: Parameters<typeof api.editItem>[3]) {
    setBusy(true);
    setError(null);
    try {
      await api.editItem(book.id, item.item_type, item.item_id, body);
      if (body.status) {
        const k = key(item);
        setJust((j) => new Set(j).add(k));
        setLingering((l) => new Set(l).add(k));
        window.setTimeout(() => {
          setLingering((l) => {
            const next = new Set(l);
            next.delete(k);
            return next;
          });
        }, 900);
      }
      items.setData(
        (items.data ?? []).map((i) => {
          if (key(i) !== key(item)) return i;
          let text = i.text;
          if (body.text) text = `${splitText(i)[0]}: ${body.text}`;
          if (body.title || body.description) {
            const [t, d] = splitText(i);
            text = `${body.title ?? t}: ${body.description ?? d}`;
          }
          return { ...i, status: body.status ?? i.status, text };
        }),
      );
      setEditing(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Keyboard workflow: j/k move, a approve, h hide, e edit.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement).tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || !current || busy) return;
      if (e.key === "j") setCursor((c) => Math.min(c + 1, rows.length - 1));
      else if (e.key === "k") setCursor((c) => Math.max(c - 1, 0));
      else if (e.key === "a" && current.status !== "verified") void act(current, { status: "verified" });
      else if (e.key === "h" && current.status !== "hidden") void act(current, { status: "hidden" });
      else if (e.key === "e" && current.item_type !== "relation") setEditing(key(current));
      else return;
      e.preventDefault();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    listRef.current?.querySelector(".review-current")?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  if (items.error) return <ErrorNote error={items.error} />;
  if (!items.data) return <Spinner />;
  const openCount = items.data.filter((i) => i.status === "unverified" || i.status === "rejected").length;

  return (
    <div className="stack">
      <ReviewBookmark book={book} value={bookmark} onChange={setBookmark} />
      <div className="review-progress">
        <div className="progress">
          <div
            className="progress-bar"
            style={{ width: `${items.data.length ? 100 - (100 * openCount) / items.data.length : 100}%` }}
          />
        </div>
        <span className="muted small">
          {openCount} te controleren van {items.data.length} items
        </span>
      </div>
      <div className="row filters">
        <select value={status} onChange={(e) => { setStatus(e.target.value as StatusFilter); setCursor(0); }}
                aria-label="Status">
          <option value="open">Te controleren</option>
          <option value="hidden">Verborgen</option>
          <option value="verified">Zichtbaar</option>
          <option value="all">Alles</option>
        </select>
        <select value={type} onChange={(e) => { setType(e.target.value as typeof type); setCursor(0); }}
                aria-label="Soort">
          <option value="all">Alle soorten</option>
          <option value="fact">Feiten</option>
          <option value="relation">Relaties</option>
          <option value="event">Gebeurtenissen</option>
        </select>
        <select
          value={chapter}
          onChange={(e) => {
            setChapter(e.target.value === "all" ? "all" : Number(e.target.value));
            setCursor(0);
          }}
          aria-label="Hoofdstuk"
        >
          <option value="all">Alle hoofdstukken</option>
          {chapters.map((c) => (
            <option key={c} value={c}>
              H{c}
            </option>
          ))}
        </select>
        <input type="search" placeholder="Zoek in tekst of citaat…" value={query}
               onChange={(e) => { setQuery(e.target.value); setCursor(0); }} aria-label="Zoeken" />
      </div>
      <p className="muted small">
        Sneltoetsen: <kbd>j</kbd>/<kbd>k</kbd> volgende/vorige · <kbd>a</kbd> goedkeuren ·{" "}
        <kbd>h</kbd> verbergen · <kbd>e</kbd> bewerken. Lezers zien alleen <em>zichtbare</em> items.
      </p>
      <ErrorNote error={error} />
      {rows.length === 0 && <p className="muted">Niets in deze selectie.</p>}
      <ul className="review" ref={listRef}>
        {rows.map((i, idx) => (
          <li
            key={key(i)}
            className={`review-${i.status} ${idx === cursor ? "review-current" : ""} ${just.has(key(i)) ? "review-just" : ""}`}
            onClick={() => setCursor(idx)}
          >
            <svg className="pen" viewBox="0 0 34 34" aria-hidden="true">
              <path d="M6 18 L14 26 L29 7" />
            </svg>
            <div className="review-head">
              <span className="chapter-tag">H{i.chapter_index}</span>
              <span className="muted small">{TYPE_LABEL[i.item_type]}</span>
              <span className={`status status-${i.status}`}>{STATUS_LABEL[i.status]}</span>
            </div>
            {editing === key(i) ? (
              <EditForm item={i} busy={busy} onCancel={() => setEditing(null)}
                        onSave={(body) => void act(i, body)} />
            ) : (
              <p>
                <span className="review-text">{displayText(i)}</span>
              </p>
            )}
            <blockquote>{i.evidence_quote}</blockquote>
            <div className="row">
              {i.status !== "verified" && (
                <button className="btn btn-ghost small" disabled={busy}
                        onClick={() => void act(i, { status: "verified" })}>
                  <kbd>a</kbd> Goedkeuren
                </button>
              )}
              {i.status !== "hidden" && (
                <button className="btn btn-ghost small" disabled={busy}
                        onClick={() => void act(i, { status: "hidden" })}>
                  <kbd>h</kbd> Verbergen
                </button>
              )}
              {i.item_type !== "relation" && editing !== key(i) && (
                <button className="btn btn-ghost small" onClick={() => setEditing(key(i))}>
                  <kbd>e</kbd> Bewerken
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EditForm({
  item,
  busy,
  onSave,
  onCancel,
}: {
  item: ReviewItem;
  busy: boolean;
  onSave: (body: { text?: string; title?: string; description?: string }) => void;
  onCancel: () => void;
}) {
  const [head, body] = splitText(item);
  const [title, setTitle] = useState(head);
  const [text, setText] = useState(body);
  return (
    <form
      className="stack edit-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (item.item_type === "event") onSave({ title, description: text });
        else onSave({ text });
      }}
    >
      {item.item_type === "event" && (
        <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Titel" maxLength={300} />
      )}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000}
                aria-label="Tekst" autoFocus />
      <div className="row">
        <button className="btn small" type="submit" disabled={busy || !text.trim()}>
          Opslaan
        </button>
        <button className="btn btn-ghost small" type="button" onClick={onCancel}>
          Annuleren
        </button>
      </div>
    </form>
  );
}
